// message.js

import React from "react";
import { formatEpochMillis } from "../utils/time.js";

const CAPTURE_METADATA_KEYS = new Set([
  'type',
  'channelName',
  'sourceChannel',
  'streamId',
  'sessionId',
  'sourcePosition',
  'ingestSource',
  'ingestTimestamp',
]);

export const formatMessageDetails = (message) => {
  const formatDecimal = (decimal) => {
    if (typeof decimal === "number") {
      return decimal.toString();
    }
    if (decimal && typeof decimal === "object" && "mantissa" in decimal && "exponent" in decimal) {
      const mantissa = typeof decimal.mantissa === "bigint" ? Number(decimal.mantissa) : decimal.mantissa;
      const exponent = typeof decimal.exponent === "bigint" ? Number(decimal.exponent) : decimal.exponent;

      const precision = Math.abs(exponent);
      return (mantissa * Math.pow(10, exponent)).toFixed(precision);
    }
    return null;
  };

  const sourceLabel = formatCaptureLabel(message);
  let details = sourceLabel ? `[${sourceLabel}] ${message.type}` : `${message.type}`;

  for (const key in message) {
    if (CAPTURE_METADATA_KEYS.has(key)) {
      continue;
    }

    switch (key) {
      case 'amount':
      case 'fxRate':
      case 'secondaryAmount':
        details += ` ${key}: ${formatDecimal(message[key])}`;
        break;
      case 'timestamp':
        if (message[key]) {
          const formattedTimestamp = formatEpochMillis(message[key]);
          details += `, ${key}: ${formattedTimestamp ?? message[key].toString()}`;
        }
        break;
      case 'legs':
        if (Array.isArray(message.legs) && message.legs.length > 0) {
          details += ', Legs: [';
          message.legs.forEach((leg) => {
            details += '{ ';
            for (const legKey in leg) {
              const value = leg[legKey];
              if (['amount', 'spotBid', 'spotOffer', 'fwdBid', 'fwdOffer', 'bid', 'offer', 'spot', 'fwd', 'price', 'secondaryAmount'].includes(legKey)) {
                details += `${legKey}: ${formatDecimal(value)}, `;
              } else {
                details += `${legKey}: ${value}, `;
              }
            }
            details = details.slice(0, -2) + ' }, ';
          });
          details = details.slice(0, -2) + ' ]';
        } else {
          details += ', Legs: None';
        }
        break;
      default:
        if (message[key]) {
          details += `, ${key}: ${message[key]}`;
        }
        break;
    }
  }

  const captureDetails = formatCaptureDetails(message);
  if (captureDetails) {
    details += `, ${captureDetails}`;
  }

  return details;
};

export const HighlightedText = ({ text, searchTerm }) => {
  if (!searchTerm) {
    return text;
  }

  const escapedSearchTerm = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const matcher = new RegExp(`(${escapedSearchTerm})`, 'gi');
  const segments = text.split(matcher);

  if (segments.length === 1) {
    return text;
  }

  return segments.map((segment, index) => (
    segment.toLowerCase() === searchTerm.toLowerCase()
      ? <mark key={`${segment}-${index}`}>{segment}</mark>
      : <React.Fragment key={`${segment}-${index}`}>{segment}</React.Fragment>
  ));
};

const Message = ({ message, searchTerm = '' }) => {
  const replacer = (key, value) => {
    if (typeof value === 'bigint') {
      return value.toString();
    }
    if (value && typeof value === 'object' && 'mantissa' in value && 'exponent' in value) {
      return { mantissa: value.mantissa, exponent: value.exponent };
    }
    return value;
  };

  const serializedMessage = JSON.stringify(message, replacer, 2);

  console.log(serializedMessage);

  return (
    <div className={`message ${message.type}`} style={{ padding: '1px', marginBottom: '1px' }}>
      <HighlightedText text={formatMessageDetails(message)} searchTerm={searchTerm} />
    </div>
  );
};

export default Message;

function formatCaptureLabel(message) {
  if (!message?.channelName) {
    return null;
  }

  if (message.streamId !== undefined && message.streamId !== null) {
    return `${message.channelName}/${message.streamId}`;
  }

  return message.channelName;
}

function formatCaptureDetails(message) {
  const details = [];

  if (message?.sourceChannel) {
    details.push(`aeronChannel: ${message.sourceChannel}`);
  }
  if (message?.sessionId !== undefined && message?.sessionId !== null) {
    details.push(`sessionId: ${message.sessionId}`);
  }
  if (message?.sourcePosition !== undefined && message?.sourcePosition !== null) {
    details.push(`sourcePosition: ${message.sourcePosition}`);
  }
  if (message?.ingestSource && message.ingestSource !== 'live') {
    details.push(`capture: ${message.ingestSource}`);
  }

  return details.join(', ');
}
